import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Grid,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Chip,
} from "@mui/material";
import {
  LocalShipping,
  EventBusy,
  PendingActions,
  LocalShippingOutlined,
  WorkHistoryOutlined,
} from "@mui/icons-material";
import { twMerge } from "tailwind-merge";
import { useSelector } from "react-redux";
import { format } from "date-fns";
import { useGetDriverDetailQuery, useLazyGetPendingTransferRequestsQuery } from "store/services/driverApi";
import { useGetDriverDashboardStatsQuery } from "store/services/driverDashboardApi";
import ToLoadBranchPanel from "./ToLoadBranchPanel";
import UnloadingBranchPanel from "./UnloadingBranchPanel";
import PendingTransferRequestsPanel from "./PendingTransferRequestsPanel";

export default function DriverProfileOverview() {
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const isDriver = user?.typeRefSource === "Driver";
  const driverId = isDriver ? user.typeRef : null;
  const { data: driverData } = useGetDriverDetailQuery(driverId, {
    skip: !driverId,
  });
  const { data: driverStatsData, isLoading: isStatsLoading } =
    useGetDriverDashboardStatsQuery(driverId);

  // Inside the component, add:
  const [fetchPendingRequests, { data: pendingRequestsData }] =
    useLazyGetPendingTransferRequestsQuery();

  useEffect(() => {
    if (driverId) fetchPendingRequests(driverId);
  }, [driverId, fetchPendingRequests]);

  const hasPendingRequests = (pendingRequestsData?.data?.length || 0) > 0;

  // Format schedule times
  const formatTime = (time) => {
    if (!time || typeof time !== "string" || !time.includes(":")) return "N/A";
    const [hours, minutes] = time.split(":").map(Number);
    if (isNaN(hours) || isNaN(minutes)) return "N/A";
    const date = new Date(2023, 0, 1, hours, minutes);
    return isNaN(date.getTime()) ? "N/A" : format(date, "h:mm a");
  };

  // Stats configuration
  const stats = [
    {
      title: "Total Shipment Collection",
      icon: <LocalShipping fontSize="large" className="text-blue-500" />,
      items: [
        {
          label: "Total Shipments",
          count: driverStatsData?.totalCollectionCount || 0,
          link: "/app/driver/myShipments",
        },
      ],
      bgColor: "bg-blue-50 dark:bg-blue-900",
    },
    {
      title: "Pending Delivery",
      icon: <PendingActions fontSize="large" className="text-blue-500" />,
      items: [
        {
          label: "Pending",
          count: driverStatsData?.pendingDeliveryCount || 0,
          link: "/app/driver/pendingDelivery",
        },
      ],
      bgColor: "bg-blue-50 dark:bg-blue-900",
    },
  ];

  // Work Schedule items
  const scheduleItems = (
    driverData?.work_schedule
      ?.filter((schedule) => schedule.isActive)
      .map((schedule) => {
        const currentDay = new Date().toLocaleString("en-US", {
          weekday: "long",
        });
        const formattedStart = formatTime(schedule?.startTime);
        const formattedEnd = formatTime(schedule?.endTime);
        let displayDay =
          schedule.scheduleType === "Flexible" && schedule.date
            ? format(new Date(schedule.date), "EEEE, MMMM d, yyyy")
            : schedule?.day || "N/A";
        return {
          label: displayDay,
          count: `${formattedStart} - ${formattedEnd}`,
          link: "/app/driver/schedule",
          isCurrentDay: schedule.day === currentDay,
        };
      }) || []
  ).sort((a, b) => {
    const dayOrder = {
      Sunday: 0,
      Monday: 1,
      Tuesday: 2,
      Wednesday: 3,
      Thursday: 4,
      Friday: 5,
      Saturday: 6,
    };
    return (dayOrder[a.label] ?? 1000) - (dayOrder[b.label] ?? 1000);
  });

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Grid container spacing={2}>
        {stats.map((stat) => (
          <Grid
            key={stat.title}
            item
            xs={12}
            sm={4}
            sx={{
              transition: "transform 0.3s ease, box-shadow 0.3s ease",
              "&:hover": {
                transform: "translateY(-4px)",
                "& .stat-paper": { boxShadow: 6 },
              },
            }}
          >
            <Paper
              className={twMerge(
                "stat-paper overflow-hidden p-2 transition-all duration-300",
                stat.bgColor
              )}
              sx={{
                borderRadius: 3,
                boxShadow: 3,
                height: "100%",
                padding: 2,
              }}
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                mb={1}
              >
                <Typography
                  variant="subtitle2"
                  fontWeight="bold"
                  className="text-gray-900 dark:text-white"
                  sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}
                >
                  {stat.title}
                </Typography>
                {stat.icon}
              </Box>
              <Box>
                {stat.items.map((item, index) => (
                  <Box
                    key={index}
                    display="flex"
                    flexDirection="column"
                    justifyContent="space-between"
                    p={0.5}
                    className="dark:hover:bg-gray-1000 cursor-pointer rounded-lg hover:bg-gray-200 hover:bg-opacity-50"
                    onClick={() => navigate(item.link)}
                  >
                    <Typography
                      variant="caption"
                      color={item.isCurrentDay ? "#3b82f6" : "textSecondary"}
                      sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                    >
                      {item.label || stat.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight="bold"
                      className="text-gray-900 dark:text-white"
                      sx={{ fontSize: { xs: "0.875rem", sm: "2rem" } }}
                    >
                      {item.count || "N/A"}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Paper>
          </Grid>
        ))}
        {/* License Card */}
        <Grid
          item
          xs={12}
          sm={4}
          sx={{
            transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
            "&:hover": {
              transform: "translateY(-8px) scale(1.02)",
              "& .license-card": {
                boxShadow: "0 20px 40px rgba(0,0,0,0.15)",
                "& .license-glow": {
                  opacity: 0.8,
                  transform: "scale(1.1)",
                },
              },
            },
          }}
        >
          <Card
            className="license-card"
            sx={{
              borderRadius: 5,
              height: "100%",
              background:
                "linear-gradient(145deg, #0f172a 0%, #1e293b 50%, #334155 100%)",
              color: "white",
              position: "relative",
              overflow: "hidden",
              boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
              cursor: "pointer",
              display: "flex",
              alignItems: "stretch",
              border: "1px solid rgba(255,255,255,0.1)",
              transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
            onClick={() => navigate("/app/driver/licence")}
          >
            <CardContent
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                position: "relative",
                zIndex: 2,
              }}
            >
              {isStatsLoading ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ color: "rgba(255,255,255,0.7)" }}
                  >
                    Loading license...
                  </Typography>
                </Box>
              ) : driverStatsData?.driverLicense && driverData?.driverId ? (
                <>
                  {/* Header Section */}
                  <Box sx={{ mb: 2 }}>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="flex-start"
                      mb={1}
                    >
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "rgba(255,255,255,0.6)",
                            fontSize: "0.7rem",
                            fontWeight: 500,
                            letterSpacing: 1.2,
                            textTransform: "uppercase",
                          }}
                        >
                          Driver ID
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily:
                              "'JetBrains Mono', 'Courier New', monospace",
                            letterSpacing: 1,
                            fontSize: { xs: "0.8rem", sm: "0.9rem" },
                            fontWeight: 600,
                            color: "#60a5fa",
                          }}
                        >
                          {driverData.driverId}
                        </Typography>
                      </Box>

                      {/* License Status Chip */}
                      <Chip
                        label={
                          new Date(driverStatsData.driverLicense.expiryDate) >
                            new Date()
                            ? "VALID"
                            : "EXPIRED"
                        }
                        size="small"
                        sx={{
                          backgroundColor:
                            new Date(driverStatsData.driverLicense.expiryDate) >
                              new Date()
                              ? "#10b981"
                              : "#ef4444",
                          color: "white",
                          fontWeight: "bold",
                          fontSize: "0.65rem",
                          letterSpacing: 0.5,
                          height: 20,
                          "& .MuiChip-label": { px: 1 },
                          boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
                        }}
                      />
                    </Box>

                    {/* License Number */}
                    <Box sx={{ textAlign: "center", my: 1 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "rgba(255,255,255,0.6)",
                          fontSize: "1rem",
                          fontWeight: 500,
                          letterSpacing: 1.8,
                          textTransform: "uppercase",
                          display: "block",
                        }}
                      >
                        {driverStatsData.driverLicense.driverName}
                      </Typography>
                      <Typography
                        variant="h6"
                        sx={{
                          fontFamily:
                            "'JetBrains Mono', 'Courier New', monospace",
                          letterSpacing: 3,
                          fontSize: { xs: "1.1rem", sm: "1.3rem" },
                          fontWeight: "bold",
                          color: "#fbbf24",
                          textShadow: "0 2px 4px rgba(0,0,0,0.3)",
                          background:
                            "linear-gradient(45deg, #fbbf24, #f59e0b)",
                          backgroundClip: "text",
                          WebkitBackgroundClip: "text",
                          WebkitTextFillColor: "transparent",
                        }}
                      >
                        {driverStatsData.driverLicense.licenceNumber}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Dates Section */}
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "rgba(255,255,255,0.6)",
                          fontSize: "0.65rem",
                          fontWeight: 500,
                          letterSpacing: 1,
                          textTransform: "uppercase",
                          display: "block",
                        }}
                      >
                        Issued
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily:
                            "'JetBrains Mono', 'Courier New', monospace",
                          letterSpacing: 0.5,
                          fontSize: { xs: "0.75rem", sm: "0.8rem" },
                          fontWeight: 600,
                          color: "#94a3b8",
                        }}
                      >
                        {format(
                          new Date(driverStatsData.driverLicense.issueDate),
                          "MMM dd, yyyy"
                        )}
                      </Typography>
                    </Box>

                    <Box sx={{ textAlign: "right" }}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "rgba(255,255,255,0.6)",
                          fontSize: "0.65rem",
                          fontWeight: 500,
                          letterSpacing: 1,
                          textTransform: "uppercase",
                          display: "block",
                        }}
                      >
                        Expires
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily:
                            "'JetBrains Mono', 'Courier New', monospace",
                          letterSpacing: 0.5,
                          fontSize: { xs: "0.75rem", sm: "0.8rem" },
                          fontWeight: 600,
                          color:
                            new Date(driverStatsData.driverLicense.expiryDate) >
                              new Date()
                              ? "#10b981"
                              : "#ef4444",
                        }}
                      >
                        {format(
                          new Date(driverStatsData.driverLicense.expiryDate),
                          "MMM dd, yyyy"
                        )}
                      </Typography>
                    </Box>
                  </Box>
                </>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%",
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      color: "rgba(255,255,255,0.4)",
                      fontStyle: "italic",
                      textAlign: "center",
                      mb: 1,
                    }}
                  >
                    No License Found
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "rgba(255,255,255,0.3)",
                      textAlign: "center",
                      fontSize: "0.8rem",
                    }}
                  >
                    License information not available
                  </Typography>
                </Box>
              )}

              {/* Enhanced Decorative Elements */}
              <Box
                className="license-glow"
                sx={{
                  position: "absolute",
                  top: -20,
                  right: -20,
                  width: 120,
                  height: 120,
                  background:
                    "radial-gradient(circle, rgba(96,165,250,0.3) 0%, transparent 70%)",
                  borderRadius: "50%",
                  opacity: 0.4,
                  transition: "all 0.4s ease",
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  bottom: -30,
                  left: -30,
                  width: 100,
                  height: 100,
                  background:
                    "radial-gradient(circle, rgba(251,191,36,0.2) 0%, transparent 70%)",
                  borderRadius: "50%",
                  opacity: 0.3,
                }}
              />

              {/* Subtle Pattern Overlay */}
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: `url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.02'%3E%3Ccircle cx='3' cy='3' r='3'/%3E%3Ccircle cx='13' cy='13' r='3'/%3E%3C/g%3E%3C/svg%3E")`,
                  zIndex: 1,
                }}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={2} mt={2}>
        <Grid item xs={12} md={6}>
          <Paper
            sx={{
              p: { xs: 2, sm: 3 },
              borderRadius: 4,
              boxShadow: 2,
              height: "100%",
            }}
          >
            <Box display="flex" alignItems="center" mb={2}>
              <LocalShippingOutlined
                sx={{ color: "primary.main", fontSize: 28, mr: 1 }}
              />
              <Typography
                variant="h6"
                fontWeight="bold"
                className="text-gray-900 dark:text-white"
              >
                Recent Deliveries
              </Typography>
            </Box>
            {isStatsLoading ? (
              <Typography variant="body2">Loading...</Typography>
            ) : driverStatsData?.recentDeliveries?.length > 0 ? (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                      >
                        Shipment ID
                      </TableCell>
                      <TableCell
                        sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                      >
                        Receiver
                      </TableCell>
                      <TableCell
                        sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                      >
                        Delivered Date
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {driverStatsData.recentDeliveries.map((delivery, index) => (
                      <TableRow
                        key={delivery?._id || index}
                        sx={{
                          "&:hover": {
                            bgcolor: (theme) =>
                              theme.palette.action.hover
                          }
                        }}
                      >
                        <TableCell
                          sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                        >
                          {delivery?.shipmentrequest?.length > 0
                            ? delivery.shipmentrequest
                              .slice(0, 2)
                              .map((req) => req.shipmentId)
                              .join(", ") +
                            (delivery.shipmentrequest.length > 2
                              ? ", ..."
                              : "")
                            : "N/A"}
                        </TableCell>
                        <TableCell>
                          <Typography
                            component="a"
                            href={
                              delivery?._id
                                ? `/app/driver/delivery/${delivery._id}/detail`
                                : "#"
                            }
                            className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
                            sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                            onClick={(e) => {
                              e.preventDefault();
                              if (delivery?._id)
                                navigate(
                                  `/app/driver/delivery/${delivery._id}/detail`
                                );
                            }}
                          >
                            {delivery?.receiverDetails?.name || "N/A"}
                          </Typography>
                        </TableCell>
                        <TableCell
                          sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                        >
                          {delivery.createdAt
                            ? format(
                              new Date(delivery.createdAt),
                              "MM/dd/yyyy h:mm a"
                            )
                            : "N/A"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                sx={{ minHeight: "150px" }}
                gap={1}
              >
                <LocalShippingOutlined
                  sx={{ fontSize: 120, color: "text.secondary" }}
                />
                <Typography variant="body2" color="textSecondary">
                  No recent deliveries found
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
        {hasPendingRequests && (
          <Grid item xs={12} md={6}>
            <PendingTransferRequestsPanel driverId={driverId} />
          </Grid>
        )}
        <Grid item xs={12} md={6}>
          <Paper
            sx={{
              p: { xs: 2, sm: 3 },
              borderRadius: 4,
              boxShadow: 2,
              height: "100%",
            }}
          >
            <Box display="flex" alignItems="center" mb={2}>
              <WorkHistoryOutlined
                sx={{ color: "secondary.main", fontSize: 28, mr: 1 }}
              />
              <Typography
                variant="h6"
                fontWeight="bold"
                className="text-gray-900 dark:text-white"
              >
                Work Schedule
              </Typography>
            </Box>
            {scheduleItems.length > 0 ? (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                      >
                        Day
                      </TableCell>
                      <TableCell
                        sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                      >
                        Time
                      </TableCell>
                      <TableCell
                        sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                      >
                        Status
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {scheduleItems.map((item, index) => (
                      <TableRow
                        key={index}
                        sx={{ "&:hover": { bgcolor: "grey.100" } }}
                      >
                        <TableCell>
                          <Typography
                            component="a"
                            href={item.link}
                            className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
                            sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                            onClick={(e) => {
                              e.preventDefault();
                              navigate(item.link);
                            }}
                          >
                            {item.label}
                          </Typography>
                        </TableCell>
                        <TableCell
                          sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                        >
                          {item.count || "N/A"}
                        </TableCell>
                        <TableCell
                          sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                        >
                          {item.isCurrentDay ? "Current" : "Scheduled"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                sx={{ minHeight: "150px" }}
                gap={1}
              >
                <EventBusy sx={{ fontSize: 120, color: "text.secondary" }} />
                <Typography variant="body2" color="textSecondary">
                  No work schedule found
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mt: 2 }}>
        <Grid item xs={12} md={6}>
          <ToLoadBranchPanel driverId={driverId} />
        </Grid>
        <Grid item xs={12} md={6}>
          <UnloadingBranchPanel driverId={driverId} />
        </Grid>

      </Grid>
    </Box>
  );
}
